// frontend/src/services/analytics.js
// Google Analytics 4 Integration for Carnivores Adapting Terribly

// Initialize Google Analytics
export const initializeGA = () => {
  // Add GA4 script to head if not already present
  if (!window.gtag) {
    // Create script element
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=G-2K67H6VW5X';
    document.head.appendChild(script);

    // Initialize dataLayer and gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', 'G-2K67H6VW5X', {
      send_page_view: true,
    });
  }
};

// Safe gtag wrapper that checks if GA is loaded
const safeGtag = (...args) => {
  if (window.gtag) {
    window.gtag(...args);
  } else {
    console.warn('Google Analytics not initialized');
  }
};

// Event tracking functions
export const trackEvent = (eventName, parameters = {}) => {
  safeGtag('event', eventName, parameters);
};

// Page view tracking
export const trackPageView = (pagePath, pageTitle) => {
  safeGtag('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle,
  });
};

// Game-specific events
export const GameAnalytics = {
  // Room/Game Creation Events
  trackGameCreated: (roomCode, settings = {}) => {
    trackEvent('game_created', {
      room_code: roomCode,
      max_players: settings.maxPlayers || 8,
      rounds_to_win: settings.roundsToWin || 5,
      timer_enabled: settings.timerEnabled || false,
      event_category: 'game_flow',
      event_label: roomCode,
    });
  },

  trackGameJoined: (roomCode, playerCount) => {
    trackEvent('game_joined', {
      room_code: roomCode,
      current_players: playerCount,
      event_category: 'game_flow',
      event_label: roomCode,
    });
  },

  // Gameplay Events
  trackGameStarted: (roomCode, playerCount) => {
    trackEvent('game_started', {
      room_code: roomCode,
      player_count: playerCount,
      event_category: 'gameplay',
      event_label: `${playerCount} players`,
      value: playerCount,
    });
  },

  trackRoundCompleted: (roomCode, roundNumber, roundDuration) => {
    trackEvent('round_completed', {
      room_code: roomCode,
      round_number: roundNumber,
      duration_seconds: Math.round(roundDuration / 1000),
      event_category: 'gameplay',
      event_label: `Round ${roundNumber}`,
      value: roundNumber,
    });
  },

  trackCardSubmitted: (roomCode, submissionTime) => {
    trackEvent('card_submitted', {
      room_code: roomCode,
      submission_time_ms: submissionTime,
      event_category: 'gameplay',
      event_label: 'player_action',
    });
  },

  trackVoteCast: (roomCode, isJudge = false) => {
    trackEvent('vote_cast', {
      room_code: roomCode,
      is_card_czar: isJudge,
      event_category: 'gameplay',
      event_label: isJudge ? 'czar_pick' : 'player_vote',
    });
  },

  trackGameCompleted: (roomCode, winner, playerCount, gameDuration, totalRounds) => {
    trackEvent('game_completed', {
      room_code: roomCode,
      winner_name: winner,
      player_count: playerCount,
      duration_minutes: Math.round(gameDuration / 60000),
      total_rounds: totalRounds,
      event_category: 'game_completion',
      event_label: `${playerCount} players, ${totalRounds} rounds`,
      value: totalRounds,
    });
  },

  // User Flow Events
  trackUserSignup: (method) => {
    trackEvent('sign_up', {
      method: method, // 'google' or 'discord'
      event_category: 'user_acquisition',
    });
  },

  trackUserLogin: (method) => {
    trackEvent('login', {
      method: method, // 'google' or 'discord'
      event_category: 'user_engagement',
    });
  },

  // Error/Drop-off Events
  trackConnectionError: (errorType, roomCode = null) => {
    trackEvent('connection_error', {
      error_type: errorType,
      room_code: roomCode,
      event_category: 'errors',
      event_label: errorType,
    });
  },

  trackPlayerDropoff: (roomCode, stage) => {
    trackEvent('player_dropoff', {
      room_code: roomCode,
      dropoff_stage: stage, // 'lobby', 'mid_game', etc.
      event_category: 'user_retention',
      event_label: stage,
    });
  },

  // Feature Usage Events
  trackFeatureUsed: (featureName, details = {}) => {
    trackEvent('feature_used', {
      feature_name: featureName,
      ...details,
      event_category: 'feature_adoption',
      event_label: featureName,
    });
  },

  // Future Monetization Events (ready when needed)
  trackTokenPurchased: (amount, tokenCount) => {
    trackEvent('purchase', {
      currency: 'USD',
      value: amount,
      items: [
        {
          item_id: 'game_token',
          item_name: 'Game Token',
          price: amount / tokenCount,
          quantity: tokenCount,
        },
      ],
      event_category: 'monetization',
    });
  },

  trackPremiumFeatureViewed: (featureName) => {
    trackEvent('view_item', {
      currency: 'USD',
      value: 0,
      items: [
        {
          item_id: featureName,
          item_name: featureName,
          item_category: 'premium_feature',
        },
      ],
      event_category: 'monetization',
    });
  },
};

// User Properties
export const setUserProperties = (properties) => {
  safeGtag('set', 'user_properties', properties);
};

// Developer Mode Management
export const setDeveloperMode = (isDeveloper = false) => {
  safeGtag('set', 'user_properties', {
    user_type: isDeveloper ? 'developer' : 'player',
    is_internal: isDeveloper ? 'true' : 'false',
  });

  // Store in localStorage for persistence
  localStorage.setItem('developerMode', isDeveloper.toString());

  if (isDeveloper) {
    console.log('🔧 GA4 Developer Mode: ENABLED');
  } else {
    console.log('👤 GA4 Developer Mode: DISABLED (tracking as regular user)');
  }
};

// Check if developer mode should be enabled
export const isDeveloperMode = () => {
  // Check multiple conditions for developer mode
  const conditions = {
    localStorage: localStorage.getItem('developerMode') === 'true',
    localhost: window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1',
    devParam: window.location.search.includes('dev=true'),
    debugParam: window.location.search.includes('debug=true'),
  };

  // Developer mode is true if ANY condition is met
  return Object.values(conditions).some((condition) => condition);
};

// Initialize developer mode based on current conditions
export const initializeDeveloperMode = () => {
  const isDev = isDeveloperMode();
  setDeveloperMode(isDev);
  return isDev;
};

// Toggle developer mode (useful for testing)
export const toggleDeveloperMode = () => {
  const currentMode = localStorage.getItem('developerMode') === 'true';
  setDeveloperMode(!currentMode);
  return !currentMode;
};

// Common user properties to track
export const UserProperties = {
  setPlayerType: (type) => {
    setUserProperties({ player_type: type }); // 'creator' or 'joiner'
  },

  setGamesPlayed: (count) => {
    setUserProperties({ games_played_count: count });
  },

  setPreferredDeck: (deckName) => {
    setUserProperties({ preferred_deck: deckName });
  },

  setAccountType: (type) => {
    setUserProperties({ account_type: type }); // 'free', 'premium', etc.
  },

  // Environment tracking
  setEnvironment: () => {
    const hostname = window.location.hostname;
    let environment = 'unknown';

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      environment = 'local_development';
    } else if (hostname.includes('staging') || hostname.includes('preview')) {
      environment = 'staging';
    } else if (hostname.includes('carnivoresadaptingterribly.com')) {
      environment = 'production';
    }

    setUserProperties({ environment: environment });
    return environment;
  },
};

// Timing utilities
export const trackTiming = (category, variable, value, label) => {
  safeGtag('event', 'timing_complete', {
    name: variable,
    value: Math.round(value), // milliseconds
    event_category: category,
    event_label: label,
  });
};

// Debug mode for development
export const enableDebugMode = () => {
  if (process.env.NODE_ENV === 'development') {
    window.gtag('config', 'G-2K67H6VW5X', {
      debug_mode: true,
    });
    console.log('GA4 Debug Mode Enabled');
  }
};

// Initialize GA on import
if (typeof window !== 'undefined') {
  initializeGA();

  // Initialize developer mode and environment
  initializeDeveloperMode();
  UserProperties.setEnvironment();

  // Enable debug mode in development
  if (process.env.NODE_ENV === 'development') {
    enableDebugMode();
  }

  // Log current tracking mode
  console.log(
    `📊 Google Analytics initialized - Mode: ${isDeveloperMode() ? 'Developer' : 'Player'}`
  );
}
